'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import { useRoomContext } from '@livekit/components-react';
import { useKrispNoiseFilter } from '@livekit/components-react/krisp';
import toast from 'react-hot-toast';
import { isLowPowerDevice } from './client-utils';
import { useVirtualBackground } from './VirtualBackgroundContext';
import {
  applyKrispBackgroundDecision,
  decideKrispForBackground,
  recoverLocalMicrophone,
} from './krisp-background-guard';
import { shouldRecoverMicrophoneAfterKrisp } from './audio-playback-unlock';

interface NoiseCancellationContextType {
  isEnabled: boolean;
  isPending: boolean;
  isSupported: boolean;
  toggle: () => void;
  setEnabled: (enabled: boolean) => void;
}

const NoiseCancellationContext = createContext<NoiseCancellationContextType | null>(null);

export function NoiseCancellationProvider({ children }: { children: React.ReactNode }) {
  const [isSupported, setIsSupported] = useState(true);
  const initializedRef = useRef(false);
  const krispPausedForBackgroundRef = useRef(false);
  const { isVirtualBackgroundActive } = useVirtualBackground();
  const room = useRoomContext();

  const { isNoiseFilterEnabled, setNoiseFilterEnabled, isNoiseFilterPending } =
    useKrispNoiseFilter({
      filterOptions: {
        bufferOverflowMs: 100,
        bufferDropMs: 200,
        quality: isLowPowerDevice() ? 'low' : 'medium',
        onBufferDrop: () => {
          console.warn(
            '[NoiseCancellation] Buffer dropped — filter may auto-disable (krisp >= 0.3.2)',
          );
        },
      },
    });

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const wasmSupported =
      typeof WebAssembly !== 'undefined' &&
      typeof WebAssembly.instantiate === 'function';

    if (!wasmSupported) {
      setIsSupported(false);
      console.warn(
        '[NoiseCancellation] WebAssembly não é suportado neste navegador. ' +
          'O cancelamento de ruído não está disponível.',
      );
      return;
    }

    setNoiseFilterEnabled(!isLowPowerDevice());
  }, [setNoiseFilterEnabled]);

  /** Krisp + fundo virtual saturam CPU — pausar com recover do mic se o filtro quebrar. */
  useEffect(() => {
    const decision = decideKrispForBackground({
      isVirtualBackgroundActive,
      isNoiseFilterEnabled,
      isPending: isNoiseFilterPending,
      isSupported,
      pausedForBackground: krispPausedForBackgroundRef.current,
    });

    if (decision.action === 'none') {
      return;
    }

    let cancelled = false;
    void (async () => {
      const result = await applyKrispBackgroundDecision(decision, setNoiseFilterEnabled);
      if (cancelled) return;

      if (decision.action === 'pause') {
        krispPausedForBackgroundRef.current = true;
        if (decision.toast) {
          toast(
            'Cancelamento de ruído pausado para melhorar a performance do fundo virtual.',
            { icon: 'ℹ️', duration: 4500 },
          );
        }
      }

      if (decision.action === 'resume' && result.ok) {
        krispPausedForBackgroundRef.current = false;
      }

      if (!result.ok) {
        console.warn('[NoiseCancellation] falha ao ajustar Krisp com fundo:', result.error);
        if (decision.action === 'resume') {
          krispPausedForBackgroundRef.current = false;
        }
      }

      const intendedOn = room?.localParticipant?.isMicrophoneEnabled === true;
      if (
        shouldRecoverMicrophoneAfterKrisp({
          krispPipelineChanged: true,
          microphoneIntendedOn: intendedOn,
        })
      ) {
        const setMic = room?.localParticipant?.setMicrophoneEnabled?.bind(room.localParticipant);
        if (setMic) {
          const recovered = await recoverLocalMicrophone(setMic);
          if (!recovered) {
            toast.error('Não foi possível manter o microfone após o fundo virtual.');
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isVirtualBackgroundActive,
    isNoiseFilterEnabled,
    isNoiseFilterPending,
    isSupported,
    setNoiseFilterEnabled,
    room,
  ]);

  const toggle = useCallback(() => {
    if (!isSupported || isNoiseFilterPending) return;
    setNoiseFilterEnabled(!isNoiseFilterEnabled);
  }, [isSupported, isNoiseFilterEnabled, isNoiseFilterPending, setNoiseFilterEnabled]);

  return (
    <NoiseCancellationContext.Provider
      value={{
        isEnabled: isNoiseFilterEnabled,
        isPending: isNoiseFilterPending,
        isSupported,
        toggle,
        setEnabled: setNoiseFilterEnabled,
      }}
    >
      {children}
    </NoiseCancellationContext.Provider>
  );
}

export function useNoiseCancellation(): NoiseCancellationContextType {
  const ctx = useContext(NoiseCancellationContext);
  if (!ctx) {
    throw new Error('useNoiseCancellation must be used inside NoiseCancellationProvider');
  }
  return ctx;
}
